import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { listPromoCodes } from '../../api/promoCodesApi';
import { getApiErrorText } from '../../utils/apiResponse';
import { promoListParams, readPromoListQuery, updatePromoListQuery } from './promoListQuery';

const typedFilters = ['search', 'id', 'assignedUserId', 'discountFrom', 'discountTo'];

export default function usePromoCodeList() {
  const history = useHistory();
  const location = useLocation();
  const query = useMemo(() => readPromoListQuery(location.search), [location.search]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const request = useRef(0);
  const previousQuery = useRef(null);
  const updateQuery = useCallback(changes => {
    const next = { pathname: location.pathname, search: updatePromoListQuery(location.search, changes) };
    if (Object.prototype.hasOwnProperty.call(changes, 'search')) history.replace(next);
    else history.push(next);
  }, [history, location.pathname, location.search]);
  useEffect(() => {
    const sequence = ++request.current;
    setLoading(true); setError('');
    const debounce = previousQuery.current && typedFilters.some(key => previousQuery.current[key] !== query[key]);
    previousQuery.current = query;
    const fetchList = () => listPromoCodes(promoListParams(query)).then(result => {
      if (sequence !== request.current) return;
      const lastPage = Math.max(1, Math.ceil(result.total / query.limit));
      if (query.page > lastPage) {
        history.replace({ pathname: location.pathname, search: updatePromoListQuery(location.search, { page: lastPage }) });
        return;
      }
      setItems(result.items); setTotal(result.total);
    }).catch(reason => {
      if (sequence === request.current) { setItems([]); setTotal(0); setError(getApiErrorText(reason)); }
    }).finally(() => { if (sequence === request.current) setLoading(false); });
    let timer;
    if (debounce) timer = setTimeout(fetchList, 200);
    else fetchList();
    return () => { clearTimeout(timer); request.current += 1; };
  }, [query, refresh, history, location.pathname, location.search]);
  return { items, total, loading, error, query, updateQuery,
    resetQuery: () => history.push({ pathname: location.pathname, search: '' }),
    reload: () => setRefresh(value => value + 1) };
}
